import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type RestaurantUser,
  type RestaurantUserCvx,
  restaurantUserSchema,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { Badge } from '@/components/ui/badge';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { cn } from '@/utils/cn';
import { RestaurantUserForm } from './forms/RestaurantUserForm';

export const Route = createFileRoute('/r/$restaurantId/s/users')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const {
    data: restaurantUsers,
    isError,
    isPending,
  } = useQuery(api.restaurantUsers.listByRestaurantId, { restaurantId });
  const [formState, setFormState] = useState<RestaurantUser | 'new' | 'closed'>('closed');

  const handleDeleteUser = async (user: RestaurantUserCvx) => {
    console.log('TODO..');
  };

  const handleCreateNew = () => {
    setFormState('new');
  };

  const handleDisableUser = async (user: RestaurantUserCvx) => {
    try {
      await convex.action(api.restaurantUsers.disableCognitoUser, {
        restaurantUserId: user._id,
        username: user.username,
      });
      toast.success('User disabled successfully');
    } catch (error) {
      toast.error("Couldn't disable user", {
        description: getUserErrorMessage(error),
      });
    }
  };

  const handleEnableUser = async (user: RestaurantUserCvx) => {
    try {
      await convex.action(api.restaurantUsers.enableCognitoUser, {
        restaurantUserId: user._id,
        username: user.username,
      });
      toast.success('User enabled successfully');
    } catch (error) {
      toast.error("Couldn't enable user", {
        description: getUserErrorMessage(error),
      });
    }
  };

  const handleSendPushNotification = async (restaurantUserId: string) => {
    try {
      await convex.mutation(api.pushNotifications.addToQueue, {
        sendToRestaurantUserId: restaurantUserId,
        expoPushNotification: {
          title: 'Test Notification',
          body: 'This is a test notification',
          badge: 0,
        },
      });
      toast.success('Push notification queued for sending');
    } catch (error) {
      toast.error("Couldn't send notification", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (isError) return null; // Handled in parent layout

  return (
    <>
      {formState !== 'closed' && (
        <RestaurantUserForm
          editRestaurantUser={formState === 'new' ? undefined : formState}
          onClose={() => setFormState('closed')}
          restaurantId={restaurantId}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardAction>
            <ActionButton onClick={handleCreateNew}>Create New</ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="w-[64px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {restaurantUsers.map((u) => {
                return (
                  <TableRow className={cn(!u.isEnabled && 'bg-muted')} key={u.username}>
                    <TableCell className="w-[100px]">
                      <Badge
                        className={cn(
                          'text-xs text-green-600 dark:text-green-400',
                          !u.isEnabled && 'text-muted-foreground dark:text-muted-foreground',
                        )}
                        variant="secondary"
                      >
                        {u.isEnabled ? 'ENABLED' : 'DISABLED'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{u.name}</div>
                      <div className="text-sm text-muted-foreground">{u.username}</div>
                    </TableCell>

                    <TableCell className="w-[64px]">
                      <ActionDropdown
                        onEdit={() =>
                          setTimeout(() => setFormState(restaurantUserSchema.decode(u)), 0)
                        }
                        // onDelete={
                        //   !u.isEnabled ? () => handleDeleteUser(restaurantId, u.sub) : undefined
                        // }
                      >
                        {u.isEnabled ? (
                          <>
                            <DropdownMenuItem onSelect={() => handleSendPushNotification(u._id)}>
                              Send test push notification
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className={
                                'hover:text-destructive! dark:hover:text-red-300! text-destructive dark:hover:bg-destructive dark:text-red-400'
                              }
                              onSelect={() => handleDisableUser(u)}
                            >
                              Disable user
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem onSelect={() => handleEnableUser(u)}>
                            Enable user
                          </DropdownMenuItem>
                        )}
                      </ActionDropdown>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
